import { ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, Check, ChevronDownIcon, ChevronsUpDown, Loader2 } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'

import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'
import * as yup from 'yup'

import { DiscountShapeIcon, TaskSquareIcon, TrashIcon } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PaginationComponent } from '@/components/ui/pagination'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { capitalizeFirstLetter } from '@/lib/helper'
import { cn } from '@/lib/utils'
import couponService from '@/services/coupon-service'
import userService from '@/services/user-service'

export interface CreateCouponForm {
	name: string
	code: string
	discount_type: string
	discount_amount: string
	usage_limit: string
	user_id: string
	expiry_date: Date
}

export const CouponTable = () => {
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [page, setPage] = useState(1)
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [isCreateDialogVisible, setIsCreateDialogVisible] = useState(false)
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')
	const [open, setOpen] = useState(false)
	const [openCalendar, setOpenCalendar] = useState(false)

	const schema = yup.object<CreateCouponForm>().shape({
		name: yup.string().required('Gutschein ist erforderlich!'),
		code: yup.string().required('Code ist erforderlich!'),
		discount_type: yup.string().required('Rabatttyp ist erforderlich!'),
		discount_amount: yup.string().required('Rabattbetrag ist erforderlich!'),
		usage_limit: yup.string().required('Nutzungsbeschränkung ist erforderlich!'),
		expiry_date: yup.date().required('Verfallsdatum ist erforderlich!'),
		user_id: yup.string().notRequired(),
	})

	const form = useForm<CreateCouponForm>({
		resolver: yupResolver(schema as any),
		defaultValues: { user_id: '' },
		mode: 'all',
	})

	const { data, refetch, isLoading } = useQuery(
		'fetch-coupons',
		() =>
			couponService.fetchCoupons({
				pageNumber: page.toString(),
				pageSize: rowsPerPage.toString(),
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching coupons')
			},
		},
	)

	const { data: users } = useQuery('fetch-users', () => userService.fetchAllUsers(), {
		refetchOnWindowFocus: false,
		enabled: isCreateDialogVisible,
		onError: () => {
			toast.error('Error fetching users')
		},
	})

	const { mutate, isLoading: creatingCoupon } = useMutation({
		mutationKey: 'create-coupon',
		mutationFn: (data: CreateCouponForm) => couponService.createCoupon(data),
	})

	const { mutate: deleteCoupon } = useMutation({
		mutationKey: 'delete-coupon',
		mutationFn: (id: Coupon['id']) => couponService.deleteCoupon(id),
	})

	useEffect(() => {
		refetch()
	}, [rowsPerPage, page])

	const sortedData = useMemo(() => {
		if (!data) return []
		if (!sortColumn) return data.data

		return [...data.data].sort((a, b) => {
			const aValue = a[sortColumn as keyof Coupon]
			const bValue = b[sortColumn as keyof Coupon]

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [data, sortColumn, sortDirection])

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const handleDeleteCoupon = (id: number) => {
		deleteCoupon(id, {
			onSuccess: () => {
				toast.success('Gutschein erfolgreich gelöscht')
				refetch()
			},
			onError: () => {
				toast.error('Gutschein konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.')
			},
		})
	}

	const onSubmit = (data: CreateCouponForm) => {
		mutate(data, {
			onSuccess: () => {
				toast.success('Gutschein erfolgreich hinzugefügt')
				form.reset()
				setIsCreateDialogVisible(false)
				refetch()
			},
			onError: () => {
				toast.error('Der Gutschein kann nicht hinzugefügt werden. Bitte versuche es erneut.')
			},
		})
	}

	const columns: ColumnDef<Coupon>[] = [
		{
			accessorKey: 'name',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Gutscheinname" />,
		},
		{
			accessorKey: 'code',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Code" />,
		},
		{
			accessorKey: 'discount_type',
			accessorFn: (row: Coupon) => (row.discount_type === 'fixed' ? 'Fest' : 'Prozentsatz'),
			header: ({ column }) => <DataTableColumnHeader column={column} title="Rabatttyp" />,
		},
		{
			accessorKey: 'discount_amount',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Rabattbetrag" />,
		},
		{
			accessorKey: 'usage_limit',
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Nutzungsbeschränkung" />
			),
		},
		{
			accessorKey: 'redeem_count',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Eingelöst" />,
		},
		{
			accessorKey: 'expiry_date',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Verfallsdatum" />,
			cell: ({ row }) => <div>{format(new Date(row.getValue('expiry_date')), 'dd-MM-yyyy')}</div>,
		},
		{
			accessorKey: 'user_id',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Benutzer" />,
		},
		{
			id: 'action-coupons',
			enableSorting: false,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Aktionen" />,
			cell: ({ row }) => (
				<div className="flex items-center justify-center gap-0.5">
					<Tooltip>
						<TooltipTrigger
							onClick={() => handleDeleteCoupon(row.original.id)}
							className="border cursor-pointer border-secondary hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<TrashIcon className="shrink-0 size-4" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Löschen</p>
						</TooltipContent>
					</Tooltip>
				</div>
			),
		},
	]

	const MobileCouponsTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('name')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Gutscheinname
								{sortColumn === 'name' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('code')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Code
								{sortColumn === 'code' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('expiry_date')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Verfallsdatum
								{sortColumn === 'expiry_date' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{sortedData?.map((coupon, index) => (
						<React.Fragment key={coupon.id.toString() + index.toString()}>
							<TableRow
								className={cn(
									'h-14',
									expandedRows.includes(coupon.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
								)}
								onClick={() => toggleRow(coupon.id)}>
								<TableCell
									align="left"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(coupon.id),
									})}>
									<div className="flex items-center">
										<ChevronRightIcon
											className={cn(
												'size-3 shrink-0 -mt-0.5',
												expandedRows.includes(coupon.id)
													? 'rotate-90 text-secondary'
													: 'text-muted-foreground',
											)}
										/>
										{coupon.name}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(coupon.id),
									})}>
									{coupon.code}
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(coupon.id),
									})}>
									{format(new Date(coupon.expiry_date), 'dd-MM-yyyy')}
								</TableCell>
							</TableRow>
							{expandedRows.includes(coupon.id) && (
								<TableRow
									className={cn(
										'mt-2',
										!expandedRows.includes(coupon.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
									)}>
									<TableCell className="pt-0" colSpan={3}>
										<div className="px-3 flex flex-col gap-4">
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Rabatttyp</h6>
												<p className="text-secondary text-xs font-medium">
													{coupon.discount_type === 'fixed' ? 'Fest' : 'Prozentsatz'}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Rabattbetrag
												</h6>
												<p className="text-secondary text-xs font-medium">
													{coupon.discount_amount}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Nutzungsbeschränkung
												</h6>
												<p className="text-secondary text-xs font-medium">{coupon.usage_limit}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Eingelöst</h6>
												<p className="text-secondary text-xs font-medium">{coupon.redeem_count}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">User ID</h6>
												<p className="text-secondary text-xs font-medium">{coupon.user_id}</p>
											</div>

											<div className="flex items-center justify-between">
												<div className="flex items-center gap-1">
													<InformationCircleIcon className="text-muted-foreground size-3" />
													<h6 className="text-muted-foreground text-[10px] font-medium">Aktion</h6>
												</div>
												<div className="flex items-center gap-1">
													<div
														onClick={() => handleDeleteCoupon(coupon.id)}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<TrashIcon className="shrink-0 max-lg:size-3" />
													</div>
												</div>
											</div>
										</div>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
			<ResponsiveModal
				open={isCreateDialogVisible}
				onOpenChange={() => {
					form.reset()
					setIsCreateDialogVisible(false)
				}}>
				<ResponsiveModalContent className="gap-0">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							GUTSCHEINE HINZUFÜGEN
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Bitte füllen Sie die folgenden Felder aus, um einen Gutschein hinzuzufügen
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="grid grid-cols-1 lg:grid-cols-2 mt-6 gap-y-6 lg:mt-10 lg:gap-x-4 lg:gap-y-5">
								<FormField
									control={form.control}
									name="name"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Name <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="20% Gutschein" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="code"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Code <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="OFF20" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="discount_type"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Rabatttyp <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<Select onValueChange={field.onChange} value={field.value}>
												<FormControl>
													<SelectTrigger>
														<SelectValue placeholder="Rabatttyp" />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													<SelectItem value="percentage">Prozentsatz</SelectItem>
													<SelectItem value="fixed">Fest</SelectItem>
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="discount_amount"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Rabattbetrag <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="20"
													inputMode="numeric"
													{...field}
													onChange={e => {
														const value = e.target.value
														if (/^\d*\.?\d*$/.test(value)) {
															field.onChange(value)
														}
													}}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="usage_limit"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Nutzungsbeschränkung <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="20"
													inputMode="numeric"
													{...field}
													onChange={e => {
														const value = e.target.value
														if (/^\d*\.?\d*$/.test(value)) {
															field.onChange(value)
														}
													}}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="expiry_date"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Verfallsdatum <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<Popover open={openCalendar} onOpenChange={setOpenCalendar}>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant="outline"
															className={cn(
																'w-full hover:bg-white max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																!field.value && 'text-muted-foreground',
															)}>
															{field.value ? (
																format(field.value, 'dd.MM.yyyy')
															) : (
																<span>dd.mm.yyyy</span>
															)}
															<ChevronDownIcon className="size-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-auto p-0 border-input" align="start">
													<Calendar
														mode="single"
														selected={field.value}
														onSelect={val => {
															field.onChange(val)
															setOpenCalendar(false)
														}}
														initialFocus
													/>
												</PopoverContent>
											</Popover>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="user_id"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">Benutzer auswählen</FormLabel>
											<Popover open={open} onOpenChange={setOpen}>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant="outline"
															role="combobox"
															className={cn(
																'hover:bg-white overflow-hidden text-ellipsis max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																!field.value && 'text-muted-foreground',
															)}>
															<p className="max-w-11/12 overflow-hidden text-ellipsis">
																{field.value
																	? `ID ${users?.find(user => user.id === Number(field.value))?.id} ${capitalizeFirstLetter(users?.find(user => user.id === Number(field.value))?.first_name ?? '')} ${capitalizeFirstLetter(users?.find(user => user.id === Number(field.value))?.last_name ?? '')} (${users?.find(user => user.id === Number(field.value))?.email})`
																	: 'Benutzer auswählen'}
															</p>

															<ChevronDownIcon className="shrink-0 size-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-[200px] p-0 border-input">
													<Command>
														<CommandInput placeholder="Benutzer suchen..." className="h-9" />
														<CommandList>
															<CommandEmpty>Kein Benutzer gefunden.</CommandEmpty>
															<CommandGroup>
																{Array.isArray(users) &&
																	users?.map(user => (
																		<CommandItem
																			value={`ID ${user.id} ${user.first_name} ${user.last_name} (
																												${user.email})`}
																			key={user.id}
																			onSelect={() => {
																				form.setValue('user_id', user.id.toString(), {
																					shouldValidate: true,
																				})
																				setOpen(false)
																			}}>
																			ID {user.id} {capitalizeFirstLetter(user.first_name)}{' '}
																			{capitalizeFirstLetter(user.last_name)} ({user.email})
																			<Check
																				className={cn(
																					'ml-auto',
																					user.id.toString() === field.value
																						? 'opacity-100'
																						: 'opacity-0',
																				)}
																			/>
																		</CommandItem>
																	))}
															</CommandGroup>
														</CommandList>
													</Command>
												</PopoverContent>
											</Popover>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button
								variant="secondary"
								className="mt-20 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{creatingCoupon && <Loader2 className="animate-spin" />}
								{creatingCoupon ? 'Bitte warten' : 'Speichern'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<div className="flex max-lg:flex-col max-lg:gap-3 lg:items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
						<TaskSquareIcon className="text-secondary" />
					</div>
					<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
						Gutschein Liste
					</h1>
				</div>
				<Button
					onClick={() => setIsCreateDialogVisible(true)}
					variant="outline"
					className="text-secondary max-lg:text-xs max-lg:h-10 font-normal border-secondary rounded-lg lg:rounded-xl">
					<DiscountShapeIcon className="size-4 lg:size-6 shrink-0" />
					GUTSCHEINE HINZUFÜGEN
				</Button>
			</div>
			<div className="flex max-lg:mt-5 mt-6 flex-col gap-3 lg:gap-4">
				<div className="grid grid-cols-1 items-center gap-[9px] lg:hidden">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-full h-10 text-xs">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
			<div className="flex max-lg:hidden items-center justify-between mt-6">
				<div className="flex items-center gap-4">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-[127px]">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-8">
					<PaginationComponent
						currentPage={page}
						totalPages={data?.pagination?.totalPages ?? 1}
						onPageChange={setPage}
					/>
				</div>
			</div>
			<div className="mt-4">
				{isSmallScreen ? (
					<MobileCouponsTable />
				) : (
					<DataTable columns={columns} data={sortedData ?? []} isLoading={isLoading} />
				)}
			</div>
			<div className="mt-6 lg:mt-16 flex justify-center">
				<PaginationComponent
					currentPage={page}
					totalPages={data?.pagination.totalPages ?? 1}
					onPageChange={setPage}
				/>
			</div>
		</div>
	)
}
